import React, { useRef, useState } from "react";
import { Search, X, MessageSquareOff } from "lucide-react";
import { AvatarRing } from "../../common/comms/AvatarRing";
import { useRoomsUnread } from "../../hooks/comms/RoomsUnreadProvider";
import { useCommsIdentity } from "../../hooks/comms/useCommsIdentity";
import { useClientInsights } from "../../hooks/insights/useClientInsights";
import { SegmentedControl } from "../../common/floatingDock/SegmentedControl";
import { FiltersButton } from "../../common/comms/rosterTags/FiltersButton";
import { FiltersPopover } from "../../common/comms/rosterTags/FiltersPopover";
import { FiltersTray } from "../../common/comms/rosterTags/FiltersTray";
import { InlineFilterRail } from "../../common/comms/rosterTags/InlineFilterRail";
import { NudgeCard } from "../../common/comms/rosterTags/NudgeCard";
import { RosterSignalChip } from "../../common/comms/rosterTags/RosterSignalChip";
import { relativeTime } from "../../utils/oversightFormat";
import { useIsMobile } from "../../hooks/useIsMobile";
import { FILTER_DEFS, FILTER_IDS } from "../../common/comms/rosterTags/rosterTagDefs";
import {
  toggleFilter,
  matchesFilters,
  countMatches,
  availableFilterIds,
  hasInsightFilter,
  compareByStalenessDesc,
  describeEmptyState,
  SEGMENTS,
  currentSegment,
  setSegment,
  countActiveInsightFilters,
} from "../../common/comms/rosterTags/applyRosterTags";

/**
 * Coaching filters offered in the Filters surface. Unread and Tagged are absent
 * on purpose — the segmented control above owns those two.
 */
const DOCK_CHIP_IDS = [
  FILTER_IDS.DIET_STALE,
  FILTER_IDS.WORKOUT_STALE,
  FILTER_IDS.NO_PLAN,
  FILTER_IDS.TRACKING,
  FILTER_IDS.NOT_TRACKING,
  FILTER_IDS.NEW,
];

/** Everything that needs a count, including the two the segments own. */
const DOCK_COUNT_IDS = [FILTER_IDS.UNREAD, FILTER_IDS.TAGGED, ...DOCK_CHIP_IDS];

/**
 * Last message time, or — for a room with no messages yet — when it was
 * created. Not `lastSeq`: that is a per-room counter, so it ranked a chatty
 * old room above yesterday's and sank every brand-new client to the bottom.
 */
const activityTime = (row) => {
  const at = row.lastMessageAt || row.createdAt;
  return at ? Date.parse(at) || 0 : 0;
};

/** Today's ordering, extracted so the staleness sort can fall through to it. */
const byMentionThenUnreadThenActivity = (a, b) => {
  if (a.mentioned !== b.mentioned) return a.mentioned ? -1 : 1;
  if ((a.unread > 0) !== (b.unread > 0)) return a.unread > 0 ? -1 : 1;
  return activityTime(b) - activityTime(a);
};

/** Stable avatar colour per client, matching ClientTableRow's derivation. */
const colorIndexFor = (id) => (id ? String(id).charCodeAt(0) % 8 : 0);

/**
 * Name, unread pill and last-message preview — identical in both row modes, so
 * it lives here rather than being duplicated into the button and the label.
 * Spans throughout: this renders inside a `<button>` in browse mode, where a
 * `<div>` would be invalid.
 */
function RowIdentity({ row }) {
  const unread = row.unread > 0;

  return (
    <span className="min-w-0 flex-1">
      <span className="flex items-baseline gap-2">
        <span
          className={`min-w-0 flex-1 truncate text-sm leading-tight ${
            unread ? "font-bold text-gray-900" : "font-medium text-gray-700"
          }`}
        >
          {row.name}
        </span>
        {row.timeLabel && (
          <span
            className={`flex-shrink-0 text-[11px] tabular-nums ${
              unread ? "font-semibold text-bc-accent-text" : "text-gray-400"
            }`}
          >
            {row.timeLabel}
          </span>
        )}
      </span>
      <span className="mt-0.5 flex items-center gap-2">
        <span
          className={`min-w-0 flex-1 truncate text-xs leading-tight ${
            row.mentioned
              ? "font-medium text-orange-primary"
              : unread
              ? "text-gray-600"
              : "text-gray-400"
          }`}
        >
          {row.lastMessage || "No messages yet"}
        </span>
        {/* One trailing slot, shared: an unread count OR a signal chip, never
            both. An unread conversation is the more urgent thing to act on. */}
        {unread ? (
          <span className="flex h-[17px] min-w-[17px] flex-shrink-0 items-center justify-center rounded-full bg-orange-primary px-1.5 text-[10px] font-bold text-white">
            {row.mentioned && <span aria-label="Mentions you">@&nbsp;</span>}
            {row.unread > 99 ? "99+" : row.unread}
          </span>
        ) : (
          <RosterSignalChip tags={row.tags} />
        )}
      </span>
    </span>
  );
}

/**
 * The dock's root view: every client the coach has a room with.
 *
 * Reads `clientRoomIndex` and `unreadByRoom` straight from
 * `RoomsUnreadProvider`, which already lists every COACHING room on boot and
 * refreshes on a 45s tick. That makes this list a pure in-memory projection —
 * no request, no spinner, no loading state, which is what lets the dock open
 * instantly.
 *
 * Deliberately NOT `InboxList`: that component fetches `/api/v1/rooms` and
 * polls every 30s of its own, which is exactly the latency this replaces.
 *
 * Two modes share the list. In browse mode a row is a button that opens the
 * conversation; in `selectable` mode it is a `<label>` wrapping a checkbox, so
 * the whole row toggles and keyboard/screen-reader behaviour comes for free.
 * The row cannot be a button in both — a checkbox inside a button is invalid
 * markup and the click targets fight.
 *
 * @param {Object} props
 * @param {(clientId: string) => void} props.onSelect
 * @param {boolean} [props.selectable=false]
 * @param {Set<string>} [props.selectedIds]
 * @param {(clientId: string) => void} [props.onToggleSelect]
 * @param {(clientIds: string[], allSelected: boolean) => void} [props.onToggleAll] -
 *   Receives the ids currently VISIBLE (post-search), never the whole roster.
 * @param {boolean} [props.expanded=false] - Wide layout: filters render inline
 *   rather than behind a trigger, since there is room to just show them.
 * @param {(clientIds: string[]) => void} [props.onNudgeMatches] - Hands the
 *   filtered clients to multi-select. Omit to hide the nudge card.
 */
export function ChatDockUserList({
  onSelect,
  selectable = false,
  selectedIds,
  onToggleSelect,
  onToggleAll,
  expanded = false,
  onNudgeMatches,
}) {
  const { clientRoomIndex, unreadByRoom, mentionRoomIds } = useRoomsUnread();
  const { getClientName } = useCommsIdentity();
  const {
    getTags,
    getInsight,
    hasInsights,
    thresholdDays,
    setThresholdDays,
  } = useClientInsights();
  const [query, setQuery] = useState("");
  // One Set holds everything. The segmented control is a single-select view
  // over two of these ids; the coaching filters AND on top.
  const [activeFilters, setActiveFilters] = useState(() => new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersAnchorRef = useRef(null);
  // The filters surface differs by platform: a popover has nowhere to go on a
  // phone, where the panel is already full-screen.
  const isMobile = useIsMobile();

  // Deliberately NOT memoized. `getClientName` and `getTags` are stable
  // callbacks that resolve a miss by enqueuing a fetch and re-rendering when it
  // lands — so a memo keyed on them would keep serving the "Client #123"
  // fallback and the empty tag set forever. `InboxList` resolves names during
  // render for the same reason, and a roster of tens of rows makes the
  // map/filter/sort free.
  const rowsAndCounts = (() => {
    const all = Array.from(clientRoomIndex.entries()).map(([clientId, room]) => ({
      clientId,
      roomId: room.roomId,
      // `client_name` is not always present on the rooms payload; the shared
      // identity cache bulk-resolves the rest, same fallback InboxList uses.
      name:
        room.clientName ||
        getClientName(clientId) ||
        `Client #${room.displayId ?? clientId}`,
      lastMessage: room.lastMessage,
      lastMessageAt: room.lastMessageAt || null,
      createdAt: room.createdAt || null,
      lastSeq: room.lastSeq || 0,
      unread: unreadByRoom.get(room.roomId) || 0,
    }));
    for (const row of all) {
      // Unread @-mention of the viewer (session-local, see RoomsUnreadProvider).
      row.mentioned = row.unread > 0 && mentionRoomIds.has(String(row.roomId));
      // Coaching signals. EMPTY_TAGS until the insights batch lands, which
      // matches no filter and renders no icon.
      row.tags = getTags(row.clientId);
      row.insight = getInsight(row.clientId);
      // From the rooms payload's `last_activity_at`. Rows without one simply
      // omit the timestamp rather than showing a placeholder.
      row.timeLabel = row.lastMessageAt ? relativeTime(row.lastMessageAt) : null;
    }

    const needle = query.trim().toLowerCase();
    const searched = needle
      ? all.filter((row) => row.name.toLowerCase().includes(needle))
      : all;

    const filtered = searched.filter((row) =>
      matchesFilters(row, row.tags, activeFilters)
    );

    // Default: mentions of you first, then unread, then most recent activity —
    // the ordering InboxList uses, plus the mention priority on top. While a
    // coaching filter is on, the question is "who have I neglected longest",
    // so staleness leads and the default order breaks ties within a band.
    const sorted = hasInsightFilter(activeFilters)
      ? filtered.sort(
          compareByStalenessDesc(
            (clientId) => getTags(clientId),
            byMentionThenUnreadThenActivity
          )
        )
      : filtered.sort(byMentionThenUnreadThenActivity);

    return {
      rows: sorted,
      // Scoped to the search-narrowed set, as before.
      counts: countMatches(searched, getTags, DOCK_COUNT_IDS),
      chipIds: availableFilterIds(searched, getTags, DOCK_CHIP_IDS),
    };
  })();

  const { rows, counts, chipIds } = rowsAndCounts;
  const emptyState = describeEmptyState(activeFilters, query);

  const activeInsightIds = [...activeFilters].filter(
    (id) => FILTER_DEFS[id]?.needsInsights
  );

  const filtersPanelProps = {
    activeIds: activeFilters,
    onToggle: (id) => setActiveFilters((prev) => toggleFilter(prev, id)),
    counts,
    chipIds,
    thresholdDays,
    onThresholdChange: setThresholdDays,
  };

  const selectedCount = selectedIds?.size ?? 0;
  // Every VISIBLE row ticked. A selection made before a search narrowed the
  // list still counts toward `selectedCount`, so the header can read
  // "unchecked" while 20 are selected — that is correct, not a glitch: the box
  // answers "is everything I can see selected?".
  const allVisibleSelected =
    rows.length > 0 && rows.every((row) => selectedIds?.has(row.clientId));

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex-shrink-0 bg-white px-2.5 pb-2.5">
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search clients"
            aria-label="Search clients"
            className="w-full rounded-full border border-gray-200 bg-input-bg py-2 pl-9 pr-8 text-sm text-gray-900 transition-colors placeholder:text-gray-400 hover:border-gray-300 focus:border-orange-primary focus:bg-white"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700"
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="mt-2.5 flex items-center gap-2">
          <SegmentedControl
            className="flex-1"
            label="Filter conversations"
            value={currentSegment(activeFilters)}
            onChange={(id) => setActiveFilters((prev) => setSegment(prev, id))}
            items={SEGMENTS.map((segment) => ({
              id: segment.id,
              label: segment.label,
              count: segment.filterId ? counts[segment.filterId] : counts.all,
            }))}
          />
          {/* Expanded shows its filters inline, so it needs no trigger. */}
          {hasInsights && !expanded && (
            <FiltersButton
              ref={filtersAnchorRef}
              expanded={filtersOpen}
              activeCount={countActiveInsightFilters(activeFilters)}
              onClick={() => setFiltersOpen((open) => !open)}
            />
          )}
        </div>
      </div>

      {hasInsights &&
        (expanded ? (
          // At 1120px the roster pane can afford to show the filters rather
          // than hide them behind a button.
          <InlineFilterRail
            activeIds={activeFilters}
            onToggle={filtersPanelProps.onToggle}
            counts={counts}
            chipIds={chipIds}
            thresholdDays={thresholdDays}
          />
        ) : isMobile ? (
          <FiltersTray open={filtersOpen} panelProps={filtersPanelProps} />
        ) : (
          <FiltersPopover
            open={filtersOpen}
            anchorRef={filtersAnchorRef}
            onClose={() => setFiltersOpen(false)}
            panelProps={filtersPanelProps}
          />
        ))}

      {selectable && rows.length > 0 && (
        /* Scoped to the FILTERED rows, not the whole roster. The search box is
           directly above, and "select all" meaning anything other than "all of
           what I can currently see" is how a message reaches the wrong people.
           `useSupportQueue.toggleSelectAll` scopes to `visible` for the same
           reason. */
        <div className="flex flex-shrink-0 items-center justify-between border-y border-gray-100 bg-white px-3.5 py-2">
          <label className="flex cursor-pointer items-center gap-2.5">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={() =>
                onToggleAll?.(
                  rows.map((row) => row.clientId),
                  allVisibleSelected
                )
              }
              className="h-4 w-4 rounded border-gray-300 accent-orange-primary focus:ring-orange-primary/30"
            />
            <span className="text-xs font-semibold text-gray-700">
              {query ? `Select ${rows.length} shown` : "Select all"}
            </span>
          </label>
          <span className="text-xs font-medium text-gray-400">
            {selectedCount > 0 ? `${selectedCount} selected` : "None selected"}
          </span>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {rows.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100">
              <MessageSquareOff
                aria-hidden="true"
                className="h-5 w-5 text-gray-400"
              />
            </span>
            <p className="text-sm font-semibold text-gray-700">{emptyState.title}</p>
            <p className="max-w-[16rem] text-xs leading-relaxed text-gray-400">
              {emptyState.body}
            </p>
          </div>
        ) : (
          <ul className="p-1.5">
            {rows.map((row) =>
              selectable ? (
                <li key={row.clientId}>
                  {/* A <label>, not a <button>: the checkbox is the control and
                      the whole row is its hit area, which also gives Space to
                      toggle and the name as the accessible label for free. */}
                  <label
                    className={`group relative flex w-full cursor-pointer items-center gap-3 rounded-xl px-2.5 py-2.5 text-left transition-colors ${
                      selectedIds?.has(row.clientId)
                        ? "bg-light-orange"
                        : "hover:bg-white hover:shadow-sm"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(selectedIds?.has(row.clientId))}
                      onChange={() => onToggleSelect?.(row.clientId)}
                      className="h-4 w-4 flex-shrink-0 rounded border-gray-300 accent-orange-primary focus:ring-orange-primary/30"
                    />
                    <AvatarRing
                      name={row.name}
                      size="md"
                      colorIndex={colorIndexFor(row.clientId)}
                    />
                    <RowIdentity row={row} />
                  </label>
                </li>
              ) : (
                <li key={row.clientId}>
                  <button
                    type="button"
                    onClick={() => onSelect(row.clientId)}
                    className={`group relative flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-left transition-colors ${
                      row.mentioned
                        ? "bg-light-orange/70 ring-1 ring-orange-primary/40 hover:bg-light-orange"
                        : "hover:bg-white hover:shadow-sm"
                    }`}
                  >
                    {/* Accent bar rather than a full background change: it marks
                        the row without washing out the unread emphasis. */}
                    <span
                      aria-hidden="true"
                      className="absolute inset-y-2 left-0 w-0.5 origin-center scale-y-0 rounded-full bg-orange-primary transition-transform duration-150 group-hover:scale-y-100 motion-reduce:transition-none"
                    />
                    <AvatarRing
                      name={row.name}
                      size="md"
                      colorIndex={colorIndexFor(row.clientId)}
                    />
                    <RowIdentity row={row} />
                  </button>
                </li>
              )
            )}
          </ul>
        )}

        {onNudgeMatches && hasInsightFilter(activeFilters) && !selectable && (
          <NudgeCard
            activeInsightIds={activeInsightIds}
            matchedClientIds={rows.map((row) => row.clientId)}
            totalCount={counts.all}
            onClearFilters={() => setActiveFilters(() => new Set())}
            onMessageAll={onNudgeMatches}
          />
        )}
      </div>
    </div>
  );
}

export default ChatDockUserList;
